import React, { useState, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  Stack,
  ImageList,
  ImageListItem,
  ImageListItemBar,
} from "@mui/material";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import styles from "@/components/measurement/additionalMeasurementUploadsModal.module.css";
import DeleteIcon from "@mui/icons-material/Delete";
import classNames from "classnames";
import ClearIcon from "@mui/icons-material/Clear";
import { TextArea } from "@/components/forms/inputs/textArea";
import { useModalDismiss } from "@/helper/customHooks/useModalDismiss";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";
import useFormSubmission from "@/helper/customHooks/useFormSubmission";
import useFileUploadProgress, {
  FileUploadRequestOptions,
} from "@/helper/customHooks/useFileUploadProgress";
import FileUploadProgress from "@/components/forms/inputs/fileUploadProgress";

type MeasurementUploadValidationTarget = "note" | "images";

interface NoteImageModalProps {
  open: boolean;
  type: "note" | "images" | null;
  onClose: () => void;
  onSubmit: (
    data: string | File[],
    id: string,
    measurementName?: string,
    options?: FileUploadRequestOptions,
  ) => Promise<void>;
  handleDeleteNote: (id: string) => void;
  handleDeleteFile: (file: any) => void;
  noteItem: any;
  imageSet: any;
  fieldName: string;
  customSubmitButtonText?: string | null;
  customCancelButtonText?: string;
  statusOfAppointment: string;
  handleError: (error: any) => void;
}

export default function NoteImageModal({
  open,
  type,
  onClose,
  onSubmit,
  noteItem,
  imageSet,
  fieldName,
  handleDeleteFile,
  handleDeleteNote,
  customCancelButtonText,
  customSubmitButtonText,
  statusOfAppointment,
  handleError,
}: NoteImageModalProps) {
  const [noteValue, setNoteValue] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [validationError, setValidationError] = useState<
    MeasurementUploadValidationTarget | null
  >(null);
  const {
    phase,
    progressPercentage,
    startUpload,
    handleUploadProgress,
    resetUploadProgress,
  } = useFileUploadProgress();
  const { registerValidationTarget, focusFirstInvalidField } =
    useFocusFirstInvalidField<
      Record<string, never>,
      MeasurementUploadValidationTarget
    >({
      fieldOrder: [],
      focusableSelector:
        'textarea:not([disabled]), [tabindex="0"], button:not([disabled])',
    });

  const { t } = useTranslation();
  let submitButtonText = t("save");
  let cancelButtonText = t("cancel");
  let dialogTitle = "";

  useEffect(() => {
    if (type === "note" && open) {
      const existingNote = noteItem?.[fieldName]?.note?.payload || "";
      setNoteValue(existingNote);
    }
    if (open) {
      setValidationError(null);
      resetUploadProgress();
    }
  }, [noteItem, fieldName, open, type, resetUploadProgress]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setUploadedFiles((prev) => [...prev, ...acceptedFiles]);
    setValidationError(null);
  }, []);

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } =
    useDropzone({
      onDrop,
      accept: { "image/*": [] },
      multiple: true,
      disabled: isUploadingImages,
    });

  const clearValueAfterSubmit = () => {
    onClose();
    setNoteValue("");
  };

  const handleSubmit = async () => {
    if (checkStatusForDeleteAllowed()) {
      return;
    }

    const invalidTarget =
      type === "note" && noteValue === ""
        ? "note"
        : type === "images" && uploadedFiles.length === 0
        ? "images"
        : null;
    if (invalidTarget) {
      setValidationError(invalidTarget);
      focusFirstInvalidField({}, [invalidTarget]);
      return;
    }

    setValidationError(null);
    try {
      if (type === "note") {
        await onSubmit(
          noteValue ?? "",
          noteItem[fieldName]?.measurement_id,
          fieldName
        );
        setNoteValue(null);
      } else {
        setIsUploadingImages(true);
        startUpload();
        await onSubmit(
          uploadedFiles,
          imageSet[fieldName]?.measurement_id,
          fieldName,
          { onUploadProgress: handleUploadProgress },
        );
        setUploadedFiles([]);
      }
      clearValueAfterSubmit();
    } catch (err) {
      handleError(err);
    } finally {
      if (type === "images") {
        setIsUploadingImages(false);
        resetUploadProgress();
      }
    }
  };

  const handleRemoveFile = (index: number) => {
    setUploadedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const styleClasses = classNames(styles.dropzoneBase, {
    [styles.dropzoneFocused]: isFocused,
    [styles.dropzoneAccept]: isDragAccept,
    [styles.dropzoneReject]: isDragReject,
  });

  const filePreview = (id: any) => {
    const url = "/api/v1/file/" + `${id}` + "/view/";
    return url;
  };

  const openPreview = (id: any) => {
    const url = "/api/v1/file/" + `${id}` + "/view/";
    window.open(url, "_blank");
  };

  const checkStatusForDeleteAllowed = () => {
    const statusArrayForDeleteAllowed = [
      "open",
      "in_progress",
      "waiting_for_lab_results",
      "evaluating_lab_results",
    ];

    if (statusArrayForDeleteAllowed.includes(statusOfAppointment)) return false;

    return true;
  };
  const { handleSubmit: handleFormSubmit, isSubmitting } = useFormSubmission({
    onSubmit: handleSubmit,
    disabled: checkStatusForDeleteAllowed(),
  });

  if (customSubmitButtonText) {
    submitButtonText = customSubmitButtonText;
  }
  if (customCancelButtonText) {
    cancelButtonText = customCancelButtonText;
  }
  if (checkStatusForDeleteAllowed()) {
    cancelButtonText = t("close");
  }
  if (type === "note") {
    dialogTitle = t("add_note");
  }
  if (type === "images") {
    dialogTitle = t("add_image");
  }
  if (checkStatusForDeleteAllowed()) {
    dialogTitle = t("review_mode");
  }

  const modalContent = (
    <Stack className={styles.stackFormWrapper}>
      {type === "note" ? (
        <>
          <TextArea
            ref={registerValidationTarget("note")}
            onChange={(e) => {
              setNoteValue(e.target.value);
              setValidationError(null);
            }}
            value={noteValue || ""}
            rows={10}
            cols={75}
            disabled={checkStatusForDeleteAllowed()}
            fieldStyle={{ resize: "both", minWidth: "100%" }}
          />
          {validationError === "note" && (
            <Typography color="error" variant="caption" role="alert">
              {t("field_required")}
            </Typography>
          )}
          {noteItem?.[fieldName]?.note &&
            Object.keys(noteItem[fieldName].note).length !== 0 && (
              <GenericButton
                color="error"
                variant="contained"
                disabled={isSubmitting || checkStatusForDeleteAllowed()}
                onClick={() => {
                  handleDeleteNote(noteItem?.[fieldName]?.note);
                  setNoteValue("");
                }}
              >
                {t("delete")}
              </GenericButton>
            )}
        </>
      ) : !checkStatusForDeleteAllowed() ? (
        <section
          className={styles.uploadWrapper}
          ref={registerValidationTarget("images")}
        >
          <div
            {...getRootProps({
              className: styleClasses,
            })}
          >
            <input {...getInputProps()} className={styles.hiddenInput} />
            <p className={styles.uploadInputText}>{t("upload_description")}</p>
            <p className={`${styles.uploadInputText} ${styles.noMargin}`}>
              {t("upload_file_types")}:
              {" .png, .jpeg, .jpg, .gif, .webp, .bmp, .svg, .tiff, .heic"}
            </p>
          </div>
          {validationError === "images" && (
            <Typography color="error" variant="caption" role="alert">
              {t("no_file_selected")}
            </Typography>
          )}
        </section>
      ) : (
        <></>
      )}
      {/* Uploaded File Previews */}
      {type === "images" && uploadedFiles.length > 0 && (
        <Grid container spacing={2} className={styles.uploadPreviewWrapper}>
          <Grid item xs={12}>
            <Typography
              variant="h6"
              textAlign={"center"}
              sx={{ marginTop: "1rem" }}
            >
              {t("images_for_upload")}
            </Typography>
          </Grid>

          {uploadedFiles.map((file, index) => (
            <Grid
              item
              key={index}
              style={{
                position: "relative",
                width: 350,
                height: 262,
              }}
            >
              <GenericButton
                variant="icon"
                color="error"
                onClick={() => handleRemoveFile(index)}
                className={styles.imageRemoveButton}
                disabled={isUploadingImages}
              >
                <ClearIcon fontSize="medium" />
              </GenericButton>
              <img
                src={URL.createObjectURL(file)}
                alt={`upload-${index}`}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: 8,
                }}
              />
            </Grid>
          ))}
        </Grid>
      )}
      {type === "images" && (
        <FileUploadProgress
          phase={phase}
          progressPercentage={progressPercentage}
          dataTestId="additional-measurement-image-upload-progress"
        />
      )}
      {/* Preview Section */}
      <Typography variant="h6" textAlign={"center"} sx={{ marginTop: "1rem" }}>
        {type === "images" &&
        imageSet?.[fieldName].image_set?.length > 0 &&
        uploadedFiles.length === 0
          ? t("measurement_all_images")
          : null}
      </Typography>

      {type === "images" && uploadedFiles.length === 0 && (
        <Grid container spacing={2} sx={{ margin: "1rem 0 0 0" }}>
          <ImageList
            sx={{
              gridAutoFlow: "column",
              gridTemplateColumns: "repeat(auto-fill, 350px) !important",
              gridAutoColumns: "350px",
            }}
            cols={1}
            gap={8}
            rowHeight={262}
          >
            {imageSet[fieldName]?.image_set?.map((file: any, idx: number) => (
              <ImageListItem key={idx}>
                <img
                  srcSet={filePreview(file.id)}
                  src={filePreview(file.id)}
                  alt={file.name}
                  loading="lazy"
                  onClick={() => openPreview(file.id)}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: 8,
                  }}
                />
                <ImageListItemBar
                  title={file.name}
                  actionIcon={
                    <GenericButton
                      variant="icon"
                      disabled={isSubmitting || checkStatusForDeleteAllowed()}
                      color="error"
                      className={styles.deleteImageIcon}
                      onClick={() => {
                        handleDeleteFile(file);
                      }}
                    >
                      <DeleteIcon />
                    </GenericButton>
                  }
                />
              </ImageListItem>
            ))}
          </ImageList>
        </Grid>
      )}
    </Stack>
  );

  const cancelClose = useModalDismiss(open, clearValueAfterSubmit, {
    enabled: !isSubmitting,
  });

  return (
    <Dialog
      open={open}
      {...cancelClose}
      PaperProps={{ sx: { maxHeight: "95%" } }}
      className={styles.dialogContainer}
      sx={{
        "& .MuiPaper-root": {
          maxWidth: "80vw",
          minWidth: "30vw",
        },
      }}
    >
      <DialogTitle textAlign="center">{dialogTitle}</DialogTitle>

      <form style={{ display: "contents" }} onSubmit={handleFormSubmit}>
        <DialogContent sx={{ overflowY: "visible" }}>
          {modalContent}
        </DialogContent>

        <DialogActions
          sx={{
            p: "1.25rem",
            justifyContent: "space-between",
          }}
        >
          <GenericButton
            variant="outlined"
            onClick={clearValueAfterSubmit}
            disabled={isSubmitting}
          >
            {cancelButtonText}
          </GenericButton>

          <GenericButton
            variant="contained"
            type="submit"
            waitingForResponse={true}
            loading={isSubmitting}
            className={classNames({
              [styles.hiddenInput]: checkStatusForDeleteAllowed(),
            })}
            showSpinnerOnDisable
            disabled={checkStatusForDeleteAllowed()}
          >
            {submitButtonText}
          </GenericButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}
